import { useState, useRef, useCallback, useLayoutEffect, useEffect } from "react";

/** "···" 按钮的预估宽度：24px 图标 + 24px 左间距 */
const MORE_BUTTON_WIDTH = 48;

/**
 * Priority+ Navigation Hook
 *
 * 根据可用宽度，计算导航菜单中可展示的项数。
 * 超出的项应放入 "···" 溢出菜单。
 *
 * 实现策略：
 * 1. SSR 渲染所有项（可见），避免 hydration 问题
 * 2. 客户端挂载后进入测量阶段：所有项渲染（invisible）用于宽度测量
 * 3. useLayoutEffect 在 paint 前完成测量并计算可见数量
 * 4. ResizeObserver 监听容器宽度变化，使用缓存的宽度值重新计算
 */
const useOverflowNav = (itemCount: number, gap = 120) => {
  const navRef = useRef<HTMLUListElement>(null);
  const itemWidthsRef = useRef<number[]>([]);
  const [visibleCount, setVisibleCount] = useState(itemCount);
  const [needsMeasure, setNeedsMeasure] = useState(false);
  const [hasMeasured, setHasMeasured] = useState(false);

  const getAvailableWidth = useCallback(() => {
    const ul = navRef.current;
    if (!ul) return Infinity;

    const headerWrapper = ul.closest("[data-header-wrapper]") as HTMLElement;
    const rightSection = headerWrapper?.querySelector("[data-header-right]") as HTMLElement;
    const leftSection = ul.closest("[data-header-left]") as HTMLElement;
    const logoEl = leftSection?.querySelector("[data-header-logo]") as HTMLElement;

    if (!headerWrapper || !rightSection) return Infinity;

    const headerWidth = headerWrapper.offsetWidth;
    const headerStyle = getComputedStyle(headerWrapper);
    const headerPadding =
      parseFloat(headerStyle.paddingLeft || "0") + parseFloat(headerStyle.paddingRight || "0");
    const rightWidth = rightSection.getBoundingClientRect().width;
    const logoWidth = logoEl ? logoEl.getBoundingClientRect().width : 0;

    return headerWidth - headerPadding - rightWidth - logoWidth - gap;
  }, [gap]);

  const calculateFitting = useCallback(
    (available: number) => {
      const widths = itemWidthsRef.current;
      if (widths.length === 0) return itemCount;

      const totalWidth = widths.reduce((sum, w) => sum + w, 0);
      if (totalWidth <= available) return itemCount;

      let cumWidth = 0;
      for (let i = 0; i < widths.length; i++) {
        cumWidth += widths[i];
        if (cumWidth + MORE_BUTTON_WIDTH > available) {
          return i;
        }
      }
      return itemCount;
    },
    [itemCount]
  );

  useEffect(() => {
    itemWidthsRef.current = [];
    setNeedsMeasure(true);
    setHasMeasured(false);
    setVisibleCount(itemCount);
  }, [itemCount]);

  // 测量阶段：paint 前同步执行，用户不会看到 invisible 状态
  useLayoutEffect(() => {
    if (!needsMeasure) return;

    const ul = navRef.current;
    if (!ul) return;

    const items = Array.from(ul.querySelectorAll(":scope > [data-nav-item]")) as HTMLElement[];
    if (items.length < itemCount) return;

    const isRTL = getComputedStyle(ul).direction === "rtl";
    itemWidthsRef.current = items.map((el) => {
      const style = getComputedStyle(el);
      const marginStart = parseFloat(isRTL ? style.marginRight : style.marginLeft) || 0;
      return el.getBoundingClientRect().width + marginStart;
    });

    const available = getAvailableWidth();
    setVisibleCount(calculateFitting(available));
    setNeedsMeasure(false);
    setHasMeasured(true);
  });

  // ResizeObserver：监听 header 容器和右侧区域，用缓存的宽度值重新计算
  useEffect(() => {
    if (needsMeasure) return;
    if (itemWidthsRef.current.length === 0) return;

    const headerWrapper = navRef.current?.closest("[data-header-wrapper]") as HTMLElement;
    if (!headerWrapper) return;

    const rightSection = headerWrapper.querySelector("[data-header-right]") as HTMLElement;

    const observer = new ResizeObserver(() => {
      const available = getAvailableWidth();
      setVisibleCount(calculateFitting(available));
    });

    observer.observe(headerWrapper);
    if (rightSection) observer.observe(rightSection);
    return () => observer.disconnect();
  }, [needsMeasure, getAvailableWidth, calculateFitting]);

  return { navRef, visibleCount, isMeasuring: needsMeasure, hasMeasured };
};

export default useOverflowNav;
